// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/hooks/useTreasury.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore'; 
import { db } from '../firebase';

export function useTreasury(userId, financialFlow, purchaseFlow) {
    const [adjustments, setAdjustments] = useState([]);

    useEffect(() => {
        if (!userId) {
            setAdjustments([]);
            return;
        }

        const collectionRef = collection(db, 'users', userId, 'treasury_adjustments');
        const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
            const data = snapshot.docs.map(doc => {
                const adj = doc.data();
                return {
                    ...adj,
                    id: doc.id,
                    date: adj.date?.toDate ? adj.date.toDate() : new Date(adj.date)
                };
            });
            data.sort((a, b) => b.date - a.date);
            setAdjustments(data);
        });

        return () => unsubscribe();
    }, [userId]);

    const addAdjustment = useCallback(async (adjustmentData) => {
        if (!userId) return;
        const newRef = doc(collection(db, 'users', userId, 'treasury_adjustments'));
        await setDoc(newRef, { ...adjustmentData, createdAt: serverTimestamp() });
    }, [userId]);

    const updateAdjustment = useCallback(async (id, updatedData) => {
        if (!userId) return;
        const ref = doc(db, 'users', userId, 'treasury_adjustments', id);
        await updateDoc(ref, updatedData);
    }, [userId]);

    const deleteAdjustment = useCallback(async (id) => {
        if (!userId) return;
        await deleteDoc(doc(db, 'users', userId, 'treasury_adjustments', id));
    }, [userId]);

    const deleteMultipleAdjustments = useCallback(async (adjustmentIds) => {
        if (!userId || !adjustmentIds || adjustmentIds.length === 0) return;
        const batch = writeBatch(db);
        adjustmentIds.forEach(id => {
            const ref = doc(db, 'users', userId, 'treasury_adjustments', id);
            batch.delete(ref);
        });
        try {
            await batch.commit();
        } catch (error) {
            console.error("Erro ao deletar em lote na tesouraria:", error);
            throw error;
        }
    }, [userId]);

    const treasuryData = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

        const safeDate = (val) => {
            if (!val) return null;
            if (val instanceof Date) return isNaN(val) ? null : val;
            if (typeof val.toDate === 'function') return val.toDate();
            const parsed = new Date(val);
            return isNaN(parsed) ? null : parsed;
        };

        const pastAdjustments = adjustments.filter(a => safeDate(a.date) <= now);
        const futureAdjustments = adjustments.filter(a => safeDate(a.date) > now);

        const totalReceivedUntilNow = (financialFlow?.receivedHistory || [])
            .filter(item => {
                const d = safeDate(item.date);
                return d && d <= endOfToday;
            })
            .reduce((acc, item) => acc + Number(item.amount || 0), 0);

        const totalPaidUntilNow = (purchaseFlow?.paidHistory || [])
            .filter(item => {
                const d = safeDate(item.date);
                return d && d <= endOfToday;
            })
            .reduce((acc, item) => acc + Number(item.amount || 0), 0);

        const totalManualInflowsUntilNow = pastAdjustments
            .filter(a => a.type === 'Entrada')
            .reduce((acc, item) => acc + Number(item.amount || 0), 0);

        const totalManualOutflowsUntilNow = pastAdjustments
            .filter(a => a.type === 'Saída')
            .reduce((acc, item) => acc + Number(item.amount || 0), 0);

        const currentBalance = (totalReceivedUntilNow + totalManualInflowsUntilNow) - (totalPaidUntilNow + totalManualOutflowsUntilNow);

        const generateProjection = () => {
            const timeline = [];

            // --- 1. HISTÓRICO PASSADO (-24 meses a -1 mês) ---
            for (let i = -24; i < 0; i++) {
                const targetDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
                const year = targetDate.getFullYear();
                const month = targetDate.getMonth();
                const endOfTargetMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);

                // Entradas reais (já recebidas até o mês alvo)
                const histIn = (financialFlow?.receivedHistory || [])
                    .filter(h => safeDate(h.date) && safeDate(h.date) <= endOfTargetMonth)
                    .reduce((acc, h) => acc + Number(h.amount || 0), 0);
                
                const manualIn = pastAdjustments
                    .filter(a => a.type === 'Entrada' && safeDate(a.date) && safeDate(a.date) <= endOfTargetMonth)
                    .reduce((acc, a) => acc + Number(a.amount || 0), 0);
                
                // Saídas reais (já pagas até o mês alvo)
                const histOut = (purchaseFlow?.paidHistory || [])
                    .filter(h => safeDate(h.date) && safeDate(h.date) <= endOfTargetMonth)
                    .reduce((acc, h) => acc + Number(h.amount || 0), 0);
                
                const manualOut = pastAdjustments
                    .filter(a => a.type === 'Saída' && safeDate(a.date) && safeDate(a.date) <= endOfTargetMonth)
                    .reduce((acc, a) => acc + Number(a.amount || 0), 0);

                // SALDO REAL NO PASSADO (Sem as dívidas)
                const pastRealizedBalance = (histIn + manualIn) - (histOut + manualOut);

                // NOVO: Cálculo das dívidas e recebíveis que estavam PENDENTES até esse mês no passado
                const pastPendingIn = (financialFlow?.receivables || [])
                    .filter(r => safeDate(r.dueDate) && safeDate(r.dueDate) <= endOfTargetMonth)
                    .reduce((acc, r) => acc + Number(r.amountToReceive || 0), 0);

                const pastPendingOut = (purchaseFlow?.payables || [])
                    .filter(p => safeDate(p.dueDate) && safeDate(p.dueDate) <= endOfTargetMonth)
                    .reduce((acc, p) => acc + Number(p.amountToPay || 0), 0);

                // SALDO PROJETADO NO PASSADO (O Saldo Real sofrendo o impacto das dívidas geradas na época)
                const pastProjectedBalance = pastRealizedBalance + pastPendingIn - pastPendingOut;

                const monthName = targetDate.toLocaleString('pt-BR', { month: 'short' });

                timeline.push({
                    label: `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${String(year).slice(-2)}`,
                    projectedBalance: pastProjectedBalance,
                    realizedBalance: pastRealizedBalance,
                    isPast: true,
                    isCurrent: false,
                    isFuture: false
                });
            }

            // --- 2. PRESENTE E FUTURO (Mês atual a +59 meses) ---
            let runningProjectedBalance = currentBalance;
            let runningRealizedBalance = currentBalance;

            const overdueIn = (financialFlow?.receivables || [])
                .filter(r => {
                    const d = safeDate(r.dueDate);
                    return d && d < startOfToday;
                })
                .reduce((acc, r) => acc + Number(r.amountToReceive || 0), 0);

            const overdueOut = (purchaseFlow?.payables || [])
                .filter(p => {
                    const d = safeDate(p.dueDate);
                    return d && d < startOfToday;
                })
                .reduce((acc, p) => acc + Number(p.amountToPay || 0), 0);

            for (let i = 0; i < 60; i++) {
                const targetDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
                const year = targetDate.getFullYear();
                const month = targetDate.getMonth();

                let pendingIn = (financialFlow?.receivables || [])
                    .filter(r => {
                        const d = safeDate(r.dueDate);
                        return d && d.getFullYear() === year && d.getMonth() === month && d >= startOfToday;
                    })
                    .reduce((acc, r) => acc + Number(r.amountToReceive || 0), 0);

                let pendingOut = (purchaseFlow?.payables || [])
                    .filter(p => {
                        const d = safeDate(p.dueDate);
                        return d && d.getFullYear() === year && d.getMonth() === month && d >= startOfToday;
                    })
                    .reduce((acc, p) => acc + Number(p.amountToPay || 0), 0);

                if (i === 0) {
                    pendingIn += overdueIn;
                    pendingOut += overdueOut;
                }

                const realizedIn = (financialFlow?.receivedHistory || [])
                    .filter(h => {
                        const d = safeDate(h.date);
                        return d && d.getFullYear() === year && d.getMonth() === month && d > endOfToday;
                    })
                    .reduce((acc, h) => acc + Number(h.amount || 0), 0);

                const realizedOut = (purchaseFlow?.paidHistory || [])
                    .filter(h => {
                        const d = safeDate(h.date);
                        return d && d.getFullYear() === year && d.getMonth() === month && d > endOfToday;
                    })
                    .reduce((acc, h) => acc + Number(h.amount || 0), 0);

                const manualFutureIn = futureAdjustments
                    .filter(a => {
                        const d = safeDate(a.date);
                        return d && d.getFullYear() === year && d.getMonth() === month && d > endOfToday;
                    })
                    .reduce((acc, a) => acc + Number(a.amount || 0), 0);

                const manualFutureOut = futureAdjustments
                    .filter(a => {
                        const d = safeDate(a.date);
                        return d && d.getFullYear() === year && d.getMonth() === month && d > endOfToday;
                    })
                    .reduce((acc, a) => acc + Number(a.amount || 0), 0);

                runningProjectedBalance = runningProjectedBalance + pendingIn + realizedIn + manualFutureIn - pendingOut - realizedOut - manualFutureOut;
                runningRealizedBalance = runningRealizedBalance + realizedIn + manualFutureIn - realizedOut - manualFutureOut;

                const monthName = targetDate.toLocaleString('pt-BR', { month: 'short' });
                timeline.push({
                    label: `${monthName.charAt(0).toUpperCase() + monthName.slice(1)}/${String(year).slice(-2)}`,
                    projectedBalance: runningProjectedBalance,
                    realizedBalance: runningRealizedBalance,
                    isPast: false,
                    isCurrent: i === 0,
                    isFuture: i > 0
                });
            }
            return timeline;
        };

        return {
            currentBalance,
            totalInflows: totalReceivedUntilNow + totalManualInflowsUntilNow,
            totalOutflows: totalPaidUntilNow + totalManualOutflowsUntilNow,
            projectedTimeline: generateProjection(),
            adjustments
        };
    }, [financialFlow, purchaseFlow, adjustments]);

    return { 
        treasuryData, 
        addAdjustment,
        updateAdjustment,
        deleteAdjustment,
        deleteMultipleAdjustments
    };
}